import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Home, Pencil, Trash2, ImagePlus, X, ChevronLeft, ChevronRight, Star } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const mediaUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

const EMPTY = {
  name: "", guests: 4, price_low: 150, price_mid: 170, price_high: 200,
  image: "", description_it: "", description_en: "", gallery: [],
};

export const AdminApartments = ({ headers }) => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(0);
  const coverInput = useRef(null);
  const galleryInput = useRef(null);

  const inputCls =
    "w-full bg-transparent border-b border-[#C29B62]/40 focus:border-[#1C3829] outline-none py-2.5 text-sm font-light placeholder:text-[#1A1A1A]/30 transition-colors";
  const labelCls = "block text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/50 mb-1";

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/apartments`);
      setList(res.data);
    } catch {
      toast.error("Errore nel caricamento degli appartamenti");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadFiles = async (files, target) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    setUploading((n) => n + arr.length);
    for (const f of arr) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const res = await axios.post(`${API}/admin/upload`, fd, { headers: headers() });
        if (target === "cover") {
          setForm((prev) => ({ ...prev, image: res.data.url }));
        } else {
          setForm((prev) => ({ ...prev, gallery: [...prev.gallery, res.data.url] }));
        }
      } catch (err) {
        const d = err.response?.data?.detail;
        toast.error(typeof d === "string" ? d : `Caricamento di ${f.name} non riuscito`);
      } finally {
        setUploading((n) => n - 1);
      }
    }
    toast.success(target === "cover" ? "Foto principale caricata" : "Foto aggiunte alla galleria");
  };

  const removeGalleryImage = (url) =>
    setForm((prev) => ({ ...prev, gallery: prev.gallery.filter((g) => g !== url) }));

  const moveGalleryImage = (url, dir) =>
    setForm((prev) => {
      const g = [...prev.gallery];
      const i = g.indexOf(url);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= g.length) return prev;
      [g[i], g[j]] = [g[j], g[i]];
      return { ...prev, gallery: g };
    });

  const makeCover = (url) =>
    setForm((prev) => ({
      ...prev,
      image: url,
      gallery: [prev.image, ...prev.gallery.filter((g) => g !== url)].filter(Boolean),
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name,
      guests: Number(form.guests),
      price_low: Number(form.price_low),
      price_mid: Number(form.price_mid),
      price_high: Number(form.price_high),
      image: form.image || null,
      description_it: form.description_it || null,
      description_en: form.description_en || null,
      gallery: form.gallery,
    };
    try {
      if (editing) {
        await axios.put(`${API}/admin/apartments/${editing}`, payload, { headers: headers() });
        toast.success("Appartamento aggiornato");
      } else {
        await axios.post(`${API}/admin/apartments`, payload, { headers: headers() });
        toast.success("Appartamento aggiunto al sito");
      }
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (a) => {
    setEditing(a.id);
    setForm({
      name: a.name, guests: a.guests,
      price_low: a.pricing.low, price_mid: a.pricing.mid, price_high: a.pricing.high,
      image: a.image || "", description_it: a.description_it || "",
      description_en: a.description_en || "", gallery: a.gallery || [],
    });
  };

  const remove = async (a) => {
    if (!window.confirm(`Eliminare "${a.name}" dal sito?`)) return;
    try {
      await axios.delete(`${API}/admin/apartments/${a.id}`, { headers: headers() });
      toast.success("Appartamento eliminato");
      if (editing === a.id) {
        setEditing(null);
        setForm(EMPTY);
      }
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore nell'eliminazione");
    }
  };

  return (
    <section data-testid="admin-apartments-section" className="border border-[#C29B62]/25 bg-white/60 p-8">
      <div className="flex items-center gap-3">
        <Home className="w-4 h-4 text-[#C29B62]" />
        <h2 className="text-2xl font-light text-[#1C3829] font-serif-display">Appartamenti</h2>
      </div>
      <p className="mt-2 text-xs text-[#1A1A1A]/50">
        Aggiungi, modifica o elimina gli alloggi mostrati sul sito. Prezzi per notte: ott–apr / mag–giu / lug–set.
        Le foto si caricano direttamente dal computer.
      </p>

      <div className="mt-6 divide-y divide-[#C29B62]/15 border-y border-[#C29B62]/25" data-testid="admin-apartments-list">
        {list.map((a) => (
          <div key={a.id} data-testid={`admin-apartment-row-${a.id}`} className="py-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              {a.image && (
                <img src={mediaUrl(a.image)} alt={a.name} className="w-14 h-10 object-cover border border-[#C29B62]/25" loading="lazy" />
              )}
              <div>
                <p className="text-base text-[#1A1A1A]">{a.name}</p>
                <p className="text-xs text-[#1A1A1A]/50">
                  {a.guests} ospiti · €{a.pricing.low} / €{a.pricing.mid} / €{a.pricing.high} · {(a.gallery || []).length} foto in galleria
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid={`admin-apartment-edit-${a.id}`}
                onClick={() => startEdit(a)}
                title="Modifica"
                className="p-2 border border-[#1C3829]/30 text-[#1C3829] hover:bg-[#1C3829] hover:text-[#F7F5F0] transition-colors"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                type="button"
                data-testid={`admin-apartment-delete-${a.id}`}
                onClick={() => remove(a)}
                title="Elimina"
                className="p-2 border border-red-800/30 text-red-800 hover:bg-red-800 hover:text-white transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="mt-8 space-y-6" data-testid="admin-apartment-form">
        <p className="text-xs uppercase tracking-[0.2em] text-[#1C3829]">
          {editing ? `Modifica: ${form.name}` : "Aggiungi un nuovo appartamento"}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="md:col-span-2">
            <label className={labelCls}>Nome</label>
            <input data-testid="apartment-name-input" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Es. Ca' Nova" />
          </div>
          <div>
            <label className={labelCls}>Ospiti max</label>
            <input data-testid="apartment-guests-input" type="number" min={1} max={20} required className={inputCls} value={form.guests} onChange={set("guests")} />
          </div>
          <div>
            <label className={labelCls}>Immagine (link https, facoltativo)</label>
            <input data-testid="apartment-image-input" className={inputCls} value={form.image} onChange={set("image")} placeholder="https://…" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-6 max-w-xl">
          <div>
            <label className={labelCls}>€ ott–apr</label>
            <input data-testid="apartment-price-low" type="number" min={1} required className={inputCls} value={form.price_low} onChange={set("price_low")} />
          </div>
          <div>
            <label className={labelCls}>€ mag–giu</label>
            <input data-testid="apartment-price-mid" type="number" min={1} required className={inputCls} value={form.price_mid} onChange={set("price_mid")} />
          </div>
          <div>
            <label className={labelCls}>€ lug–set</label>
            <input data-testid="apartment-price-high" type="number" min={1} required className={inputCls} value={form.price_high} onChange={set("price_high")} />
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <p className={labelCls}>Foto</p>
            <button
              type="button"
              data-testid="apartment-cover-upload"
              onClick={() => coverInput.current?.click()}
              disabled={uploading > 0}
              className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#1C3829] underline underline-offset-4 hover:text-[#C29B62] transition-colors disabled:opacity-40"
            >
              <ImagePlus className="w-4 h-4" /> Carica foto principale
            </button>
            <button
              type="button"
              data-testid="apartment-gallery-upload"
              onClick={() => galleryInput.current?.click()}
              disabled={uploading > 0}
              className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#1C3829] underline underline-offset-4 hover:text-[#C29B62] transition-colors disabled:opacity-40"
            >
              <ImagePlus className="w-4 h-4" /> Aggiungi foto alla galleria
            </button>
            {uploading > 0 && <span className="text-xs text-[#C29B62]">Caricamento in corso… ({uploading})</span>}
          </div>
          <input ref={coverInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="apartment-cover-file" onChange={(e) => { uploadFiles(e.target.files, "cover"); e.target.value = ""; }} />
          <input ref={galleryInput} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" data-testid="apartment-gallery-file" onChange={(e) => { uploadFiles(e.target.files, "gallery"); e.target.value = ""; }} />

          {form.image && (
            <div className="flex items-center gap-4">
              <img src={mediaUrl(form.image)} alt="Copertina" data-testid="apartment-cover-preview" className="w-28 h-20 object-cover border border-[#1C3829]" />
              <span className="text-xs text-[#1A1A1A]/50">Foto principale</span>
            </div>
          )}

          {form.gallery.length > 0 && (
            <div className="flex flex-wrap gap-3" data-testid="apartment-gallery-admin">
              {form.gallery.map((url, i) => (
                <div key={url + i} className="relative group">
                  <img src={mediaUrl(url)} alt={`Galleria ${i + 1}`} className="w-20 h-14 object-cover border border-[#C29B62]/25" loading="lazy" />
                  <button
                    type="button"
                    data-testid={`apartment-gallery-star-${i}`}
                    onClick={() => makeCover(url)}
                    title="Rendi principale"
                    className="absolute -top-2 -left-2 p-1 bg-[#C29B62] text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Star className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    data-testid={`apartment-gallery-remove-${i}`}
                    onClick={() => removeGalleryImage(url)}
                    title="Rimuovi"
                    className="absolute -top-2 -right-2 p-1 bg-red-800 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    data-testid={`apartment-gallery-left-${i}`}
                    onClick={() => moveGalleryImage(url, -1)}
                    title="Sposta a sinistra"
                    className="absolute bottom-1 left-1 p-1 bg-[#101f17]/70 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronLeft className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    data-testid={`apartment-gallery-right-${i}`}
                    onClick={() => moveGalleryImage(url, 1)}
                    title="Sposta a destra"
                    className="absolute bottom-1 right-1 p-1 bg-[#101f17]/70 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={labelCls}>Descrizione (italiano)</label>
            <textarea data-testid="apartment-desc-it" rows={3} className={`${inputCls} resize-none`} value={form.description_it} onChange={set("description_it")} />
          </div>
          <div>
            <label className={labelCls}>Descrizione (inglese)</label>
            <textarea data-testid="apartment-desc-en" rows={3} className={`${inputCls} resize-none`} value={form.description_en} onChange={set("description_en")} />
          </div>
        </div>
        <div className="flex gap-4">
          <button
            data-testid="apartment-save-button"
            type="submit"
            disabled={saving || uploading > 0}
            className="bg-[#1C3829] text-[#F7F5F0] text-xs uppercase tracking-[0.25em] px-8 py-3.5 hover:text-[#C29B62] transition-colors disabled:opacity-40"
          >
            {editing ? "Salva modifiche" : "Aggiungi appartamento"}
          </button>
          {editing && (
            <button
              type="button"
              data-testid="apartment-cancel-edit"
              onClick={() => { setEditing(null); setForm(EMPTY); }}
              className="text-xs uppercase tracking-[0.25em] px-6 py-3.5 border border-[#C29B62]/40 text-[#1A1A1A]/60 hover:text-[#1C3829] transition-colors"
            >
              Annulla
            </button>
          )}
        </div>
      </form>
    </section>
  );
};
